/**
 * The sign-in sheet's decisions as pure functions (architecture §14.77), so the rules that keep a
 * minted builder key from being lost are tested without a DOM. `SignInSheet.tsx` applies them.
 *
 * The rule: **a minted key is never lost.** `accounts.create` returns the key once; from that
 * moment it is either stored on this browser (the default: the owner goes straight on to the
 * wizard, which sets up a way back in) or shown on the save-your-key step (only when "Keep me
 * signed in on this browser" is unchecked, so the key would otherwise die with the tab), never dropped.
 */

/** `key` is the save-your-key step, `passkey` the offer to add one right after the account is created (§14.89). */
export type SheetMode = 'create' | 'signin' | 'key' | 'passkey';

export type SheetState = {
  /** The sheet is shown (the caller's `open` prop). */
  open: boolean;
  mode: SheetMode;
  /** The key `accounts.create` returned, while the save-your-key step shows it. */
  minted: string | null;
  /** `accounts.create` is in flight. */
  creating: boolean;
};

/** What a dismissal (Escape, the backdrop, ×) does on the current screen. */
export type DismissAction =
  /** A create is in flight: the dialog stays open (the key would otherwise arrive with nowhere to go). */
  | 'refuse'
  /** The save-your-key step or the passkey offer: dismissing counts as "continue", the key is stored. */
  | 'store'
  /** Create or sign-in: the sheet closes, nothing happened. */
  | 'close';

export function dismissAction(s: Pick<SheetState, 'mode' | 'minted' | 'creating'>): DismissAction {
  if (s.creating) return 'refuse';
  if ((s.mode === 'key' || s.mode === 'passkey') && s.minted !== null) return 'store';
  return 'close';
}

/** What to do with the key `accounts.create` just returned. */
export type MintedKeyAction =
  /** The sheet is open and "Keep me signed in" is unchecked: the key step, since this tab is the key's only home. */
  | 'show'
  /**
   * The sheet is open, the key will be kept, and this device has a platform authenticator (Face ID,
   * Touch ID, Windows Hello, …): offer the passkey now, one tap, before the builder (§14.89). The key
   * is held in the sheet's state until the offer is answered; every way out stores it.
   */
  | 'offer'
  /**
   * Store it at once and go on to the builder: the default without a platform authenticator (§14.88
   * — this browser keeps the key, and the wizard's "A way back in" step offers a passkey or an
   * email), and always when the sheet was closed meanwhile (Chromium closes on a repeated Escape
   * whatever `cancel` says).
   */
  | 'store';

export function mintedKeyAction(s: Pick<SheetState, 'open'> & { keep: boolean; platformPasskeys: boolean }): MintedKeyAction {
  if (!s.open) return 'store';
  if (!s.keep) return 'show';
  return s.platformPasskeys ? 'offer' : 'store';
}

/** Whether the × button renders, and whether it works. */
export type CloseButton =
  /** The save-your-key step and the passkey offer have no ×: their buttons are "I saved it — continue" / "Not now". */
  | 'hidden'
  /** A create is in flight. */
  | 'disabled'
  | 'enabled';

export function closeButton(s: Pick<SheetState, 'mode' | 'creating'>): CloseButton {
  if (s.mode === 'key' || s.mode === 'passkey') return 'hidden';
  if (s.creating) return 'disabled';
  return 'enabled';
}
